import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Mail, MapPin, Target, Shield, Clock, TrendingUp, X } from 'lucide-react';
import { sendWebhook } from '../utils/webhook';
import type { FormData } from '../types';
import SubmissionPopup from '../components/SubmissionPopup';
import { useLocation } from 'react-router-dom';

const services = [
  { id: 'email-marketing', text: 'Email Marketing' },
  { id: 'content-creation', text: 'Content Creation' },
  { id: 'brand-design', text: 'Brand Design' },
  { id: 'analytics-reporting', text: 'Analytics & Reporting' },
  { id: 'creative-strategy', text: 'Creative Strategy' },
  { id: 'social-strategy', text: 'Social Strategy' }
];

const sources = [
  { id: 'google', text: 'Google Search' },
  { id: 'social', text: 'Social Media' },
  { id: 'referral', text: 'Referral' },
  { id: 'linkedin', text: 'LinkedIn' },
  { id: 'email', text: 'Email' },
  { id: 'other', text: 'Other' }
];

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [otherSource, setOtherSource] = useState('');
  const location = useLocation();
  const selectedService = location.state?.selectedService || '';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    const form = e.currentTarget;
    
    const source = form.elements.namedItem('source') as HTMLSelectElement;
    const finalSource = source.value === 'other' ? otherSource : source.value;
    
    const formData: FormData = {
      type: 'contact',
      timestamp: new Date().toISOString(),
      source: 'contact_page',
      name: (form.elements.namedItem('name') as HTMLInputElement).value,
      email: (form.elements.namedItem('email') as HTMLInputElement).value,
      website: (form.elements.namedItem('website') as HTMLInputElement).value,
      company: (form.elements.namedItem('company') as HTMLInputElement).value,
      budget: (form.elements.namedItem('budget') as HTMLSelectElement).value,
      service: (form.elements.namedItem('service') as HTMLSelectElement).value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement).value,
      referralSource: finalSource
    };

    try {
      await sendWebhook(formData);
      form.reset();
      setShowPopup(true);
      setOtherSource('');
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'other') {
      setShowSourceModal(true);
    }
  };

  return (
    <div className="page-container">
      <div className="content-container">
        <div className="text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="float-slow"
          >
            Let's create something <span className="text-golden text-shadow-glow">amazing</span> together.
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-300 float-medium"
          >
            But first, let's get introduced.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-card-gradient p-8 rounded-2xl shadow-lg border border-gray-800">
              <h2 className="text-2xl font-bold mb-6">Get in touch:</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address <span className="text-golden">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-300 mb-2">
                    Website URL <span className="text-golden">*</span>
                  </label>
                  <input
                    type="url"
                    id="website"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name <span className="text-golden">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                    What's Your Monthly Budget? <span className="text-golden">*</span>
                  </label>
                  <select
                    id="budget"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  >
                    <option value="">Select your budget range</option>
                    <option value="1000-5000">€1,000 - €5,000</option>
                    <option value="5000-10000">€5,000 - €10,000</option>
                    <option value="10000-15000">€10,000 - €15,000</option>
                    <option value="15000+">€15,000+</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                    Service Interested In <span className="text-golden">*</span>
                  </label>
                  <select
                    id="service"
                    defaultValue={selectedService}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  >
                    <option value="">Select a service</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="source" className="block text-sm font-medium text-gray-300 mb-2">
                    How did you hear about us? <span className="text-golden">*</span>
                  </label>
                  <select
                    id="source"
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    onChange={handleSourceChange}
                    required
                  >
                    <option value="">Select an option</option>
                    {sources.map(source => (
                      <option key={source.id} value={source.id}>
                        {source.text}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Message <span className="text-golden">*</span>
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-golden text-white rounded-full font-semibold hover:opacity-90 transition-opacity"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </form>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold mb-6">Are we right for each other?</h2>
              <p className="text-gray-300 mb-8">
                We're in the business of generating ROI for our customers. We work best with companies that we can generate a minimum of 350% ROI for.
              </p>
              <p className="text-golden font-semibold text-lg mb-8">
                Interested? Let's get to work.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-golden/10 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-golden" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Email</h3>
                  <p className="text-gray-300">contact@dsmarketing.fr</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-golden/10 p-3 rounded-full">
                  <MapPin className="w-6 h-6 text-golden" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Location</h3>
                  <p className="text-gray-300">20 Avenue de Bardanac, Bordeaux</p>
                </div>
              </div>
            </div>

            <div className="bg-card-gradient p-6 rounded-2xl border border-gray-800">
              <h3 className="font-semibold mb-4 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-golden" />
                Quick Response
              </h3>
              <p className="text-gray-300">
                We typically respond within 24 hours during business days. For urgent matters, please call us directly.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center">What's <span className="text-golden">Important</span> to Us</h2>
          <div className="space-y-8">
            <div className="bg-card-gradient p-6 rounded-2xl border border-gray-800">
              <div className="flex items-start space-x-4">
                <div className="bg-golden/10 p-3 rounded-full">
                  <Target className="w-6 h-6 text-golden" />
                </div>
                <div>
                  <p className="text-gray-300">
                    We engage with companies in industries with a Total Addressable Market (TAM) of €15,000/mo+ in digital marketing potential.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card-gradient p-6 rounded-2xl border border-gray-800">
              <div className="flex items-start space-x-4">
                <div className="bg-golden/10 p-3 rounded-full">
                  <Shield className="w-6 h-6 text-golden" />
                </div>
                <div>
                  <p className="text-gray-300">
                    We prioritize working with companies that demonstrate strong Environmental, Social, and Governance (ESG) practices. Our partners are committed to delivering exceptional products while maintaining ethical business practices.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card-gradient p-6 rounded-2xl border border-gray-800">
              <div className="flex items-start space-x-4">
                <div className="bg-golden/10 p-3 rounded-full">
                  <Clock className="w-6 h-6 text-golden" />
                </div>
                <div>
                  <p className="text-gray-300">
                    We offer a flexible 30-day notice period within a 6-month commitment structure. This framework allows for sustainable growth while maintaining flexibility for your business needs.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-card-gradient p-6 rounded-2xl border border-gray-800">
              <div className="flex items-start space-x-4">
                <div className="bg-golden/10 p-3 rounded-full">
                  <TrendingUp className="w-6 h-6 text-golden" />
                </div>
                <div>
                  <p className="text-gray-300">
                    We focus on measurable outcomes, not just deliverables. While others might offer cheaper content creation, we specialize in generating real business results and ROI for our clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Other Source Modal */}
      <AnimatePresence>
        {showSourceModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card-gradient rounded-2xl p-6 max-w-md w-full border border-gray-800"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Specify Source</h3>
                <button
                  onClick={() => setShowSourceModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <input
                type="text"
                value={otherSource}
                onChange={(e) => setOtherSource(e.target.value)}
                placeholder="Please specify where you heard about us"
                className="w-full px-4 py-3 rounded-lg bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white mb-4"
              />
              <button
                onClick={() => setShowSourceModal(false)}
                className="w-full px-6 py-3 bg-golden text-white rounded-full font-semibold hover:opacity-90 transition-opacity"
              >
                Confirm
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SubmissionPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </div>
  );
}